import type { CmLineCategory, CmLineDefinition, CmLineLabel, CmLineTerm, ContributionMarginYearBreakdown } from "models";

type CmLineSourceRow = Pick<ContributionMarginYearBreakdown, "revenue" | "costs" | "contributionMargin">;

/** Every per-year /run value a line can read; breakdown leaves are read at avg. A new source is added here only. */
export const CM_LINE_SOURCES = {
  "revenue.revenueAdjusted": (row: CmLineSourceRow) => row.revenue?.revenueAdjusted,
  "revenue.subsidies": (row: CmLineSourceRow) => row.revenue?.subsidies,
  "revenue.strawRevenue": (row: CmLineSourceRow) => row.revenue?.strawRevenue,
  "breakdown.cropYield": (row: CmLineSourceRow) => row.contributionMargin?.breakdown?.yieldAndQuality?.cropYield?.avg,
  "breakdown.qualityImprovement": (row: CmLineSourceRow) => row.contributionMargin?.breakdown?.yieldAndQuality?.qualityImprovement?.avg,
  "breakdown.marketPriceTrend": (row: CmLineSourceRow) => row.contributionMargin?.breakdown?.marketPriceTrend?.avg,
  "breakdown.marketCostTrend": (row: CmLineSourceRow) => row.contributionMargin?.breakdown?.marketCostTrend?.avg,
  "breakdown.nResilience": (row: CmLineSourceRow) => row.contributionMargin?.breakdown?.costSavings?.nResilience?.avg,
  "breakdown.pestLossAvoided": (row: CmLineSourceRow) => row.contributionMargin?.breakdown?.costSavings?.pestLossAvoided?.avg,
  "breakdown.leachingReduction": (row: CmLineSourceRow) => row.contributionMargin?.breakdown?.resilience?.leachingReduction?.avg,
  "breakdown.runoffReduction": (row: CmLineSourceRow) => row.contributionMargin?.breakdown?.resilience?.runoffReduction?.avg,
  "costs.seedsMain": (row: CmLineSourceRow) => row.costs?.seedsMain,
  "costs.seedsCover": (row: CmLineSourceRow) => row.costs?.seedsCover,
  "costs.fertiliser": (row: CmLineSourceRow) => row.costs?.fertiliser,
  "costs.plantProtection": (row: CmLineSourceRow) => row.costs?.plantProtection,
  "costs.machinery": (row: CmLineSourceRow) => row.costs?.machinery,
  "costs.diesel": (row: CmLineSourceRow) => row.costs?.diesel,
  "costs.cleaning": (row: CmLineSourceRow) => row.costs?.cleaning,
  "costs.dryingStorage": (row: CmLineSourceRow) => row.costs?.dryingStorage,
  "costs.insurance": (row: CmLineSourceRow) => row.costs?.insurance,
  "costs.landRental": (row: CmLineSourceRow) => row.costs?.landRental,
  "costs.irrigation": (row: CmLineSourceRow) => row.costs?.irrigation,
  "costs.tillage": (row: CmLineSourceRow) => row.costs?.tillage,
  "costs.coverCropSowing": (row: CmLineSourceRow) => row.costs?.coverCropSowing,
  "costs.legumeCredit": (row: CmLineSourceRow) => row.costs?.legumeCredit,
  "costs.inputCostTrendShares.seedsMain": (row: CmLineSourceRow) => row.costs?.inputCostTrendShares?.seedsMain,
  "costs.inputCostTrendShares.fertiliser": (row: CmLineSourceRow) => row.costs?.inputCostTrendShares?.fertiliser,
  "costs.inputCostTrendShares.plantProtection": (row: CmLineSourceRow) => row.costs?.inputCostTrendShares?.plantProtection,
  "costs.inputCostTrendShares.tillage": (row: CmLineSourceRow) => row.costs?.inputCostTrendShares?.tillage,
  "costs.inputCostTrendShares.fieldWorkDiesel": (row: CmLineSourceRow) => row.costs?.inputCostTrendShares?.fieldWorkDiesel,
  "costs.inputCostTrendShares.coverCropSowing": (row: CmLineSourceRow) => row.costs?.inputCostTrendShares?.coverCropSowing,
  "costs.inputCostTrendShares.legumeCredit": (row: CmLineSourceRow) => row.costs?.inputCostTrendShares?.legumeCredit,
  "costs.inputCostTrendShares.nitrogenKept": (row: CmLineSourceRow) => row.costs?.inputCostTrendShares?.nitrogenKept,
  "costs.inputCostTrendShares.leaching": (row: CmLineSourceRow) => row.costs?.inputCostTrendShares?.leaching,
  "costs.inputCostTrendShares.runoff": (row: CmLineSourceRow) => row.costs?.inputCostTrendShares?.runoff,
  "costs.inputCostTrendShares.pesticideSavings": (row: CmLineSourceRow) => row.costs?.inputCostTrendShares?.pesticideSavings,
  "revenue.priceTrendShares.cropSales": (row: CmLineSourceRow) => row.revenue?.priceTrendShares?.cropSales,
  "revenue.priceTrendShares.harvestChange": (row: CmLineSourceRow) => row.revenue?.priceTrendShares?.harvestChange,
  "revenue.priceTrendShares.qualityChange": (row: CmLineSourceRow) => row.revenue?.priceTrendShares?.qualityChange,
  "revenue.priceTrendShares.straw": (row: CmLineSourceRow) => row.revenue?.priceTrendShares?.straw,
} satisfies Record<string, (row: CmLineSourceRow) => number | undefined>;

export type CmLineSourceKey = keyof typeof CM_LINE_SOURCES;

const directional = (key: string): CmLineLabel => ({ up: `${key}Up`, down: `${key}Down`, same: `${key}Same` });
const plus = (source: CmLineSourceKey): CmLineTerm => ({ source, sign: 1 });
const minus = (source: CmLineSourceKey): CmLineTerm => ({ source, sign: -1 });

/** The KH-1084 line map, in display order. Revenue is positive and costs negative, so the lines sum to the CM. */
export const CM_LINE_MAP: readonly CmLineDefinition[] = [
  {
    id: "cropSales",
    category: "revenue",
    subCategory: "cropSales",
    marker: "farm",
    label: "cropSales",
    terms: [plus("revenue.revenueAdjusted")],
    trendShares: [minus("revenue.priceTrendShares.cropSales")],
  },
  {
    id: "harvestChange",
    category: "revenue",
    subCategory: "cropSales",
    marker: "model",
    label: directional("harvestChange"),
    terms: [plus("breakdown.cropYield")],
    trendShares: [minus("revenue.priceTrendShares.harvestChange")],
  },
  {
    id: "qualityChange",
    category: "revenue",
    subCategory: "cropSales",
    marker: "model",
    label: directional("qualityChange"),
    terms: [plus("breakdown.qualityImprovement")],
    trendShares: [minus("revenue.priceTrendShares.qualityChange")],
  },
  { id: "areaPayment", category: "revenue", subCategory: "payments", marker: "farm", label: "areaPayment", terms: [plus("revenue.subsidies")] },
  // Always 0 today and absent from /run: kept so it shows once it is paid.
  { id: "klimPayment", category: "revenue", subCategory: "payments", marker: "farm", label: "klimPayment", terms: [] },
  {
    id: "straw",
    category: "revenue",
    subCategory: "straw",
    marker: "farm",
    label: directional("straw"),
    terms: [plus("revenue.strawRevenue")],
    trendShares: [minus("revenue.priceTrendShares.straw")],
  },
  {
    id: "marketPriceTrend",
    category: "revenue",
    subCategory: "marketPriceTrend",
    marker: "trend",
    label: "marketPriceTrend",
    terms: [plus("breakdown.marketPriceTrend")],
    isTrendOf: "price",
    keepWhenZero: true,
  },
  {
    id: "seed",
    category: "costs",
    subCategory: "seed",
    marker: "farm",
    label: "seed",
    terms: [minus("costs.seedsMain")],
    trendShares: [plus("costs.inputCostTrendShares.seedsMain")],
  },
  {
    id: "fertiliser",
    category: "costs",
    subCategory: "fertiliser",
    marker: "farm",
    label: "fertiliser",
    terms: [minus("costs.fertiliser")],
    trendShares: [plus("costs.inputCostTrendShares.fertiliser")],
  },
  {
    id: "legumeCredit",
    category: "costs",
    subCategory: "fertiliser",
    marker: "model",
    label: "legumeCredit",
    terms: [plus("costs.legumeCredit")],
    trendShares: [minus("costs.inputCostTrendShares.legumeCredit")],
  },
  {
    id: "nitrogenKept",
    category: "costs",
    subCategory: "fertiliser",
    marker: "model",
    label: "nitrogenKept",
    terms: [plus("breakdown.nResilience")],
    trendShares: [minus("costs.inputCostTrendShares.nitrogenKept")],
  },
  {
    id: "leaching",
    category: "costs",
    subCategory: "fertiliser",
    marker: "model",
    label: "leaching",
    terms: [plus("breakdown.leachingReduction")],
    trendShares: [minus("costs.inputCostTrendShares.leaching")],
  },
  {
    id: "runoff",
    category: "costs",
    subCategory: "fertiliser",
    marker: "model",
    label: "runoff",
    terms: [plus("breakdown.runoffReduction")],
    trendShares: [minus("costs.inputCostTrendShares.runoff")],
  },
  {
    id: "plantProtection",
    category: "costs",
    subCategory: "plantProtection",
    marker: "farm",
    label: "plantProtection",
    terms: [minus("costs.plantProtection")],
    trendShares: [plus("costs.inputCostTrendShares.plantProtection")],
  },
  {
    id: "pesticideSavings",
    category: "costs",
    subCategory: "plantProtection",
    marker: "model",
    label: "pesticideSavings",
    terms: [plus("breakdown.pestLossAvoided")],
    trendShares: [minus("costs.inputCostTrendShares.pesticideSavings")],
  },
  {
    id: "tillage",
    category: "costs",
    subCategory: "machineryFuel",
    marker: "farm",
    label: directional("tillage"),
    terms: [minus("costs.tillage")],
    trendShares: [plus("costs.inputCostTrendShares.tillage")],
  },
  {
    id: "otherFieldWork",
    category: "costs",
    subCategory: "machineryFuel",
    marker: "farm",
    label: directional("otherFieldWork"),
    // Machinery and diesel already include the tillage and cover crop passes, which have lines of their own.
    terms: [minus("costs.machinery"), minus("costs.diesel"), plus("costs.tillage"), plus("costs.coverCropSowing")],
    trendShares: [plus("costs.inputCostTrendShares.fieldWorkDiesel")],
  },
  {
    id: "dryingStorage",
    category: "costs",
    subCategory: "dryingStorageInsurance",
    marker: "farm",
    label: "dryingStorage",
    terms: [minus("costs.cleaning"), minus("costs.dryingStorage")],
  },
  {
    id: "insurance",
    category: "costs",
    subCategory: "dryingStorageInsurance",
    marker: "farm",
    label: "insurance",
    terms: [minus("costs.insurance")],
  },
  { id: "landRent", category: "costs", subCategory: "landRent", marker: "farm", label: "landRent", terms: [minus("costs.landRental")] },
  {
    id: "coverCropSeed",
    category: "costs",
    subCategory: "coverCrop",
    marker: "farm",
    label: "coverCropSeed",
    terms: [minus("costs.seedsCover")],
  },
  {
    id: "coverCropSowing",
    category: "costs",
    subCategory: "coverCrop",
    marker: "farm",
    label: "coverCropSowing",
    terms: [minus("costs.coverCropSowing")],
    trendShares: [plus("costs.inputCostTrendShares.coverCropSowing")],
  },
  // The model's irrigated mm priced at the API's water cost; 0 on a rainfed field.
  {
    id: "irrigation",
    category: "costs",
    subCategory: "irrigation",
    marker: "model",
    label: directional("irrigation"),
    terms: [minus("costs.irrigation")],
    keepWhenZero: true,
  },
  {
    id: "inputCostTrend",
    category: "costs",
    subCategory: "inputCostTrend",
    marker: "trend",
    label: "inputCostTrend",
    terms: [plus("breakdown.marketCostTrend")],
    isTrendOf: "cost",
    keepWhenZero: true,
  },
];

export const CM_LINE_CATEGORY_ORDER: readonly CmLineCategory[] = ["revenue", "costs"];

/** A line within €0.50 for both scenarios in every year counts as the same in both. */
export const CM_LINE_SAME_TOLERANCE = 0.5;

/** The shown lines must sum to the CM within this, per scenario and year. */
export const CM_LINE_RECONCILE_TOLERANCE = 2;
